// src/components/block/liveStreamManagement/SourceAliasConfigModal.tsx
// v2.35 — Validation différée : les changements ne sont appliqués qu'au clic
//          sur "Valider". "Annuler" (ou la croix) ferme sans rien toucher.

import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Settings } from 'lucide-react';
import { VideoSource, SLOT_COUNT, JingleSlot, LayoutSlot } from '../../../contexts/MediaStreamContext';

// ── Types exportés ────────────────────────────────────────────────────────────

export type SourceAliasMap = Record<string, string>;

// ── Persistance localStorage ──────────────────────────────────────────────────

const STORAGE_KEY = 'mediartc_source_aliases';

export function loadSourceAliases(): SourceAliasMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as SourceAliasMap;
  } catch { return {}; }
}

export function saveSourceAliases(aliases: SourceAliasMap): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(aliases)); } catch {}
}

export function resolveSourceLabel(source: VideoSource, aliases: SourceAliasMap): string {
  const alias = aliases[source.id];
  if (alias?.trim()) return alias.trim();
  return source.label;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMOJI_RE = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\ufe0f\s]+/u;
function cleanLabel(raw: string): string { return raw.replace(EMOJI_RE, '').trim(); }

// ── Valeur synthétique d'un slot ──────────────────────────────────────────────
// "" | "<sourceId>" | "scene:<id>"
type SlotValue = string;

function slotValueFromState(
  videoSrc: VideoSource | null,
  layout: LayoutSlot | null,
): SlotValue {
  if (layout)   return `scene:${layout.id}`;
  if (videoSrc) return videoSrc.id;
  return '';
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface SourceAliasConfigModalProps {
  availableSources:    VideoSource[];
  deckSlots:           (VideoSource | null)[];
  deckSlotLayouts:     (LayoutSlot | null)[];
  aliases:             SourceAliasMap;
  onAliasesChange:     (aliases: SourceAliasMap) => void;
  onSlotSourceChange?: (slotIndex: number, newSource: VideoSource | null) => void;
  onSlotLayoutChange?: (slotIndex: number, layout: LayoutSlot | null) => void;
  onClose:             () => void;
  jingleSlots?:        JingleSlot[];
  layoutSlots?:        LayoutSlot[];
  activeJingleId?:     string | null;
  activeLayoutId?:     string | null;
  onTriggerJingle?:    (slot: JingleSlot) => void;
  onToggleLayout?:     (slot: LayoutSlot) => void;
}

// ── Composant ─────────────────────────────────────────────────────────────────

export const SourceAliasConfigModal: React.FC<SourceAliasConfigModalProps> = ({
  availableSources, deckSlots, deckSlotLayouts, aliases, onAliasesChange,
  onSlotSourceChange, onSlotLayoutChange, onClose,
  layoutSlots = [],
  activeLayoutId = null,
  onToggleLayout,
}) => {
  // ── State local — snapshot à l'ouverture ──────────────────────────────────
  const [localAliases, setLocalAliases] = useState<SourceAliasMap>({ ...aliases });

  const [localSlotValues, setLocalSlotValues] = useState<SlotValue[]>(
    Array.from({ length: SLOT_COUNT }, (_, i) =>
      slotValueFromState(deckSlots[i] ?? null, deckSlotLayouts[i] ?? null)
    )
  );

  // ── Changements locaux (pas de callback parent) ───────────────────────────

  const handleSlotChange = useCallback((slotIndex: number, value: SlotValue) => {
    setLocalSlotValues(prev => {
      const next = [...prev];
      // Retirer la source des autres slots si source vidéo classique déjà assignée
      if (value && !value.startsWith('scene:')) {
        for (let i = 0; i < next.length; i++) {
          if (i !== slotIndex && next[i] === value) next[i] = '';
        }
      }
      next[slotIndex] = value;
      return next;
    });
  }, []);

  const handleAliasChange = useCallback((sourceId: string, value: string) => {
    setLocalAliases(prev => ({ ...prev, [sourceId]: value }));
  }, []);

  const handleReset = useCallback((sourceId: string) => {
    setLocalAliases(prev => {
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });
  }, []);

  // ── Valider — applique tout en une fois ───────────────────────────────────

  const handleConfirm = useCallback(() => {
    // 1. Aliases
    onAliasesChange(localAliases);

    // 2. Slots — on compare avec l'état initial pour n'appeler que ce qui a changé
    const initialValues = Array.from({ length: SLOT_COUNT }, (_, i) =>
      slotValueFromState(deckSlots[i] ?? null, deckSlotLayouts[i] ?? null)
    );

    for (let i = 0; i < SLOT_COUNT; i++) {
      const prev = initialValues[i];
      const next = localSlotValues[i] ?? '';
      if (prev === next) continue;

      if (next.startsWith('scene:')) {
        const id     = next.slice('scene:'.length);
        const layout = layoutSlots.find(l => l.id === id) ?? null;
        onSlotLayoutChange?.(i, layout);
        onSlotSourceChange?.(i, null);
        continue;
      }

      // Source vidéo classique (ou vide)
      const newSrc = next ? (availableSources.find(s => s.id === next) ?? null) : null;
      onSlotLayoutChange?.(i, null);
      onSlotSourceChange?.(i, newSrc);
    }

    onClose();
  }, [
    localAliases, localSlotValues,
    deckSlots, deckSlotLayouts,
    availableSources, layoutSlots,
    onAliasesChange, onSlotSourceChange, onSlotLayoutChange,
    onClose,
  ]);

  // ── Annuler — ferme sans rien faire ──────────────────────────────────────
  const handleCancel = useCallback(() => onClose(), [onClose]);

  // ── Helpers d'affichage ───────────────────────────────────────────────────

  const availableScenes  = layoutSlots.filter(l => l.background.type !== 'none' || l.incrustations.length > 0);

  const slots = Array.from({ length: SLOT_COUNT }, (_, i) => ({
    index: i,
    value: localSlotValues[i] ?? '',
  }));

  const videoSourceForValue = (value: SlotValue): VideoSource | null => {
    if (!value || value.startsWith('scene:')) return null;
    return availableSources.find(s => s.id === value) ?? null;
  };

  const aliasPlaceholder = (value: SlotValue): string => {
    if (value.startsWith('scene:')) {
      const id = value.slice('scene:'.length);
      return layoutSlots.find(l => l.id === id)?.label ?? '—';
    }
    const src = videoSourceForValue(value);
    return src ? (cleanLabel(src.label) || src.id) : '—';
  };

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <style>{`
        .sam-select option,
        .sam-select optgroup {
          background: #1a2035 !important;
          color: #e2e8f0 !important;
        }
        .sam-select optgroup {
          color: #94a3b8 !important;
          font-weight: 600;
        }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'rgb(15,23,42)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, width: 680, maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* En-tête */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Settings style={{ width: 14, height: 14, color: 'rgba(148,163,184,0.7)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Configuration des sources</span>
          </div>
          {/* Croix = annuler */}
          <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(148,163,184,0.7)', fontSize: 20, lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>

        {/* En-tête colonnes */}
        <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 28px', gap: 8, padding: '8px 16px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <span style={colHeaderStyle}>#</span>
          <span style={colHeaderStyle}>Source affectée</span>
          <span style={colHeaderStyle}>Alias</span>
          <span />
        </div>

        {/* Liste */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {slots.map(({ index, value }) => {
            const videoSrc     = videoSourceForValue(value);
            const sourceId     = videoSrc?.id ?? '';
            const currentAlias = sourceId ? (localAliases[sourceId] ?? '') : '';
            const hasCustom    = !!currentAlias.trim();
            const hasValue     = !!value;
            const canAlias     = !!videoSrc;

            return (
              <div
                key={index}
                style={{ display: 'grid', gridTemplateColumns: '48px 1fr 1fr 28px', alignItems: 'center', gap: 8, padding: '7px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: hasValue ? 'transparent' : 'rgba(255,255,255,0.01)' }}
              >
                {/* N° slot */}
                <span style={{ fontSize: 13, fontWeight: 700, color: hasValue ? '#fff' : 'rgba(255,255,255,0.2)', textAlign: 'center', fontFamily: 'monospace' }}>
                  {index + 1}
                </span>

                {/* Dropdown source */}
                <select
                  className="sam-select"
                  value={value}
                  onChange={e => handleSlotChange(index, e.target.value)}
                  style={{
                    background: '#0f172a',
                    border: `1px solid ${hasValue ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 5, color: '#e2e8f0', fontSize: 11,
                    padding: '4px 8px', outline: 'none', width: '100%',
                    boxSizing: 'border-box' as const, cursor: 'pointer',
                    appearance: 'auto' as const,
                    colorScheme: 'dark',
                    WebkitTextFillColor: '#e2e8f0',
                  }}
                >
                  <option value="">— Aucune —</option>

                  {availableSources.filter(s => s.type !== 'screen').length > 0 && (
                    <optgroup label="Sources vidéo">
                      {availableSources.filter(s => s.type !== 'screen').map(s => (
                        <option key={s.id} value={s.id}>
                          {cleanLabel(s.label) || s.id}
                        </option>
                      ))}
                    </optgroup>
                  )}

                  {availableSources.filter(s => s.type === 'screen' && s.screenActive).length > 0 && (
                    <optgroup label="Écran">
                      {availableSources.filter(s => s.type === 'screen' && s.screenActive).map(s => (
                        <option key={s.id} value={s.id}>
                          {cleanLabel(s.label) || s.id}
                        </option>
                      ))}
                    </optgroup>
                  )}

                  {availableScenes.length > 0 && (
                    <optgroup label="Scènes">
                      {availableScenes.map(l => (
                        <option key={`scene:${l.id}`} value={`scene:${l.id}`}>
                          {cleanLabel(l.label) || l.id}{activeLayoutId === l.id ? ' ●' : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>

                {/* Alias (sources vidéo uniquement) */}
                <input
                  type="text"
                  placeholder={aliasPlaceholder(value)}
                  value={currentAlias}
                  disabled={!canAlias}
                  onChange={e => { if (sourceId) handleAliasChange(sourceId, e.target.value); }}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: `1px solid ${hasCustom ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 5, color: canAlias ? '#fff' : 'rgba(255,255,255,0.2)',
                    fontSize: 11, padding: '4px 8px', fontFamily: 'monospace',
                    outline: 'none', width: '100%', boxSizing: 'border-box' as const,
                    transition: 'border-color 0.15s',
                    cursor: canAlias ? 'text' : 'default',
                    opacity: canAlias ? 1 : 0.4,
                  }}
                />

                {/* Reset alias */}
                <button
                  onClick={() => { if (sourceId) handleReset(sourceId); }}
                  disabled={!hasCustom}
                  title="Réinitialiser l'alias"
                  style={{ background: 'none', border: 'none', cursor: hasCustom ? 'pointer' : 'default', color: hasCustom ? 'rgba(148,163,184,0.7)' : 'rgba(148,163,184,0.15)', fontSize: 14, lineHeight: 1, padding: '0 2px', transition: 'color 0.15s' }}
                >
                  ↺
                </button>
              </div>
            );
          })}
        </div>

        {/* Pied — Annuler + Valider */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button
            onClick={handleCancel}
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#94a3b8', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.4)', borderRadius: 6, color: '#a5b4fc', fontSize: 12, fontWeight: 700, padding: '6px 20px', cursor: 'pointer' }}
          >
            Valider
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const colHeaderStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700,
  color: 'rgba(148,163,184,0.5)',
  textTransform: 'uppercase', letterSpacing: '0.5px',
};

export default SourceAliasConfigModal;
